import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";

import { CaseDataEntry } from "pwapi";

import {
  completeDataEntry,
  holdDataEntry,
} from "shared/actions/admin/adminCaseActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { useAppDispatch, useAppSelector } from "store/hooks";

const dataEntryTypeToButtonName = {
  [CaseDataEntry.type.BILLING]: "Billing Entry",
  [CaseDataEntry.type.DATA_ENTRY]: "Data Entry",
};

const DataEntry = ({
  data_entry,
  case_id,
  holdable,
}: {
  data_entry: CaseDataEntry;
  case_id: number;
  holdable?: boolean;
}) => {
  const billingEntry = useAppSelector((state) =>
    selectFeatureFlag(state, "billingEntry")
  );
  const dispatch = useAppDispatch();
  const caseId = case_id || data_entry.case_id;

  const loadingDataEntry = useIsLoading(completeDataEntry, {
    id: caseId,
  });
  const holdingDataEntry = useIsLoading(holdDataEntry, {
    id: caseId,
  });
  const handleCompleteDataEntry = () => {
    caseId &&
      dispatch(
        completeDataEntry({
          id: caseId,
          requestBody: {
            type: data_entry.type,
          },
        })
      );
  };

  const handleHoldDataEntry = () => {
    caseId &&
      dispatch(
        holdDataEntry({
          id: caseId,
          requestBody: {
            type: data_entry.type,
          },
        })
      );
  };

  const completed = Boolean(
    (data_entry.completed_at && !data_entry.held_at) ||
      (data_entry.completed_at &&
        data_entry.held_at &&
        data_entry.completed_at > data_entry.held_at)
  );
  const held = Boolean(
    (!data_entry.completed_at && data_entry.held_at) ||
      (data_entry.completed_at &&
        data_entry.held_at &&
        data_entry.held_at > data_entry.completed_at)
  );

  const buttonsAreVisible =
    data_entry.type === CaseDataEntry.type.BILLING ? billingEntry : true;

  return (
    <div
      className={classNames("actions__CaseDataEntry", {
        "actions__CaseDataEntry--completed": completed,
        "actions__CaseDataEntry--held": held,
      })}
    >
      {!completed && buttonsAreVisible && (
        <PathologyWatchButton
          onClick={
            data_entry.completed_at ? undefined : handleCompleteDataEntry
          }
          disabled={loadingDataEntry || holdingDataEntry || !!completed}
          className="pwButton--qaReview"
        >
          <>
            Complete{" "}
            {
              dataEntryTypeToButtonName[
                data_entry.type || CaseDataEntry.type.DATA_ENTRY
              ]
            }
          </>
        </PathologyWatchButton>
      )}

      {completed && data_entry.completed_at && (
        <div className="actions__CasesDataEntry__completed">
          <div>
            <FontAwesomeIcon
              icon={"file-circle-check"}
              color={"var(--green)"}
              fontSize={20}
            />
          </div>
          <div>
            <span>
              {
                dataEntryTypeToButtonName[
                  data_entry.type || CaseDataEntry.type.DATA_ENTRY
                ]
              }{" "}
              by {data_entry.completed_by?.first_name}{" "}
              {data_entry.completed_by?.last_name}
            </span>
            <br />
            <span>
              {DateTime.fromISO(data_entry.completed_at).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </span>
          </div>
        </div>
      )}

      {held && data_entry.held_at && (
        <div className="actions__CasesDataEntry__held">
          <div>
            <FontAwesomeIcon
              icon={"file-circle-exclamation"}
              color={"var(--red)"}
              fontSize={20}
            />
          </div>
          <div>
            <span>
              {
                dataEntryTypeToButtonName[
                  data_entry.type || CaseDataEntry.type.DATA_ENTRY
                ]
              }{" "}
              Held by {data_entry.held_by?.first_name}{" "}
              {data_entry.held_by?.last_name}
            </span>
            <br />
            <span>
              {DateTime.fromISO(data_entry.held_at).toLocaleString(
                DateTime.DATETIME_SHORT
              )}
            </span>
          </div>
        </div>
      )}

      {holdable && !held && !completed && buttonsAreVisible && (
        <PathologyWatchButton
          onClick={data_entry.held_at ? undefined : handleHoldDataEntry}
          disabled={loadingDataEntry || held}
          className="pwButton--qaReview"
        >
          <>
            Hold{" "}
            {
              dataEntryTypeToButtonName[
                data_entry.type || CaseDataEntry.type.DATA_ENTRY
              ]
            }
          </>
        </PathologyWatchButton>
      )}
    </div>
  );
};

export default DataEntry;
